import { styled } from '@mui/system';
import { useViewport } from 'contexts/viewport';
import { isEqual } from 'lodash';
import { memo, useLayoutEffect, useRef, useState } from 'react';
import Button from 'shell/components/Button';
import Icon from 'shell/components/Icon';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';

const truncateToMaxLines = (htmlString, maxLines) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;

    const paragraphs = Array.from(tempDiv.querySelectorAll('p'));
    if (paragraphs.length <= maxLines) {
        return htmlString;
    }

    const truncated = paragraphs
        .slice(0, maxLines - 1)
        .map((p) => p.outerHTML)
        .join('');

    const lastParagraph = paragraphs[maxLines - 1];
    const lastText = lastParagraph?.textContent.trim();

    // append ellipsis inline with the last visible paragraph
    return `${truncated}<p>${lastText} ...</p>`;
};

const ViewMore = memo(
    ({ text, maxLines = 4, textStyles = {}, ...textProps }) => {
        const [isExpanded, setIsExpanded] = useState(false);
        const [isOverflowing, setIsOverflowing] = useState(false);
        const [displayText, setDisplayText] = useState(text);
        const textRef = useRef(null);

        const toggleExpand = () => setIsExpanded((prev) => !prev);

        const { width } = useViewport();

        useLayoutEffect(() => {
            if (textRef.current) {
                const computedStyle = window.getComputedStyle(textRef.current);
                const fontSize = parseFloat(computedStyle.fontSize);
                const lineHeight =
                    computedStyle.lineHeight === 'normal' ? fontSize * 1.2 : parseFloat(computedStyle.lineHeight);
                const maxAllowedHeight = lineHeight * maxLines;
                const scrollHeight = textRef.current.scrollHeight;

                if (!isExpanded) {
                    const truncatedText = truncateToMaxLines(text, maxLines);
                    setDisplayText(truncatedText);
                    setIsOverflowing(scrollHeight > maxAllowedHeight);
                } else {
                    setDisplayText(text);
                }
            }
        }, [text, maxLines, width, isExpanded]);

        return (
            <Stack alignItems={'flex-start'} spacing={2}>
                <TextWrapper
                    {...textProps}
                    isExpanded={isExpanded}
                    maxLines={maxLines}
                    textStyles={textStyles}
                    ref={textRef}
                >
                    {displayText}
                </TextWrapper>
                {isOverflowing && (
                    <Button
                        color='neutral'
                        variant='borderless'
                        onClick={toggleExpand}
                        endIcon={isExpanded ? <Icon kind={'remove'} /> : <Icon kind={'add'} />}
                    >
                        {isExpanded ? 'Show Less' : 'Show More'}
                    </Button>
                )}
            </Stack>
        );
    },
    (prevProps, nextProps) => isEqual(prevProps, nextProps)
);

const TextWrapper = styled(Text)(({ isExpanded, maxLines, textStyles }) => ({
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    WebkitLineClamp: isExpanded ? 'none' : maxLines,
    wordBreak: 'break-word', // Forces long words to break
    overflowWrap: 'break-word', // Alternative for better compatibility
    ...textStyles,
}));

export default ViewMore;
